// covered-by: frontends/carrie/src/routes/settings/routes/knowledge-database/components/__tests__/CitationInlinePreview.test.tsx
import { Flex, Spinner } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { PdfCitationPreview } from "./PdfCitationPreview";

import { useObjectUrl } from "../../../../../hooks/useObjectUrl";
import { type KnowledgeRuleCitationResult } from "../../../../../utils/parsers/knowledge";
import { useSourceFileQuery } from "../../../../../utils/queries/knowledge";
import { SourcePinDot } from "../../../../ask-puntt/components/SourcePinDot";

/**
 * Inline (no-click) visual for the selected citation: the cited PDF page
 * with its bbox highlight / pin, or the cited image with a pin. Quote-only
 * citations and non-visual file types render nothing — the citations list
 * below still opens the full SourceViewerDialog for those. Errors are
 * silent for the same reason: this is a progressive enhancement, never a
 * blocker. Renders from the per-source byte cache, so swapping between
 * citations of the same source never re-downloads the file.
 */
export function CitationInlinePreview({
  citation,
  fileName,
}: {
  citation: KnowledgeRuleCitationResult;
  fileName?: string;
}) {
  const { t } = useTranslation("ui");
  const location = citation.location;
  const hasPage = location.pageNumber != null;
  const hasPin = location.rel_x != null && location.rel_y != null;

  const file = useSourceFileQuery(
    hasPage || hasPin ? citation.sourceId : undefined,
  );
  const mimeType = file.data?.mimeType ?? "";
  const imageUrl = useObjectUrl(
    mimeType.startsWith("image/") ? file.data?.bytes : undefined,
    mimeType,
  );

  if (!hasPage && !hasPin) return null;

  if (file.isLoading) {
    return (
      <Flex
        justify="center"
        className="py-4"
        data-testid="knowledge-rule-citation-inline-loading"
      >
        <Spinner />
      </Flex>
    );
  }

  const bytes = file.data?.bytes;
  if (!bytes) return null;

  if (mimeType === "application/pdf" && hasPage) {
    return (
      <div data-testid="knowledge-rule-citation-inline-preview">
        <PdfCitationPreview
          data={bytes}
          pageNumber={location.pageNumber ?? 1}
          relX={location.rel_x}
          relY={location.rel_y}
          bbox={location.bbox}
          testIdPrefix="knowledge-rule-inline"
        />
      </div>
    );
  }

  if (mimeType.startsWith("image/") && hasPin && imageUrl) {
    return (
      <div
        className="relative inline-block"
        data-testid="knowledge-rule-citation-inline-preview"
      >
        <img
          src={imageUrl}
          alt={fileName ?? "Cited source"}
          className="max-w-full"
          data-testid="knowledge-rule-citation-inline-image"
        />
        <SourcePinDot
          aria-label={t("settings.citationInlinePreview.citationLocation")}
          // why: hasPin already requires both coordinates, so a ?? 0 fallback is a branch this render cannot take.
          relX={location.rel_x as number}
          relY={location.rel_y as number}
          data-testid="knowledge-rule-citation-inline-pin"
        />
      </div>
    );
  }

  return null;
}
